import { useEffect, useRef, useState } from "react";
import { getAccess } from "@/lib/api";
import { sfx } from "@/lib/sfx";
import { usePlayer } from "@/hooks/usePlayer";

/**
 * Google Cast sender (Chromecast) — loads the framework on first mount, keeps
 * the current track pushed to the receiver whenever `current` changes.
 *
 * We use the built-in *default media receiver* (app id CC1AD845) so we don't
 * have to register + host our own custom receiver — good enough for MP3.
 *
 * AirPlay isn't controllable from JS on iOS Safari; the native "AirPlay" menu
 * appears automatically because we set `x-webkit-airplay="allow"` on the
 * shared `<audio>` element in PlayerContext.
 */

declare global {
  interface Window {
    __onGCastApiAvailable?: (available: boolean) => void;
    cast?: any;
    chrome?: { cast?: any };
  }
}

const FRAMEWORK_SRC =
  "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
let scriptInjected = false;

function loadFramework(): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.cast?.framework) return Promise.resolve(true);
  if (scriptInjected) {
    return new Promise((r) => {
      const iv = setInterval(() => {
        if (window.cast?.framework) { clearInterval(iv); r(true); }
      }, 200);
      setTimeout(() => { clearInterval(iv); r(!!window.cast?.framework); }, 5000);
    });
  }
  scriptInjected = true;
  return new Promise((resolve) => {
    window.__onGCastApiAvailable = (available: boolean) => {
      if (!available) { resolve(false); return; }
      const ctx = window.cast!.framework.CastContext.getInstance();
      ctx.setOptions({
        receiverApplicationId: window.chrome!.cast!.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: window.chrome!.cast!.AutoJoinPolicy.ORIGIN_SCOPED,
      });
      resolve(true);
    };
    const s = document.createElement("script");
    s.src = FRAMEWORK_SRC; s.async = true;
    document.head.appendChild(s);
  });
}

function absoluteStreamUrl(trackId: string): string {
  const base = window.location.origin;
  const token = getAccess() ?? "";
  // encode the JWT in the URL so the Cast receiver can fetch it without auth
  return `${base}/api/stream/${trackId}?cast_token=${encodeURIComponent(token)}`;
}

export function CastButton() {
  const { current } = usePlayer();
  const [state, setState] = useState<"unavailable" | "idle" | "connecting" | "connected">("unavailable");
  const sessionRef = useRef<any>(null);

  useEffect(() => {
    void loadFramework().then((ok) => {
      if (!ok) { setState("unavailable"); return; }
      const ctx = window.cast!.framework.CastContext.getInstance();
      const sync = () => {
        const st = ctx.getCastState();
        if (st === "NO_DEVICES_AVAILABLE") setState("unavailable");
        else if (st === "NOT_CONNECTED") setState("idle");
        else if (st === "CONNECTING") setState("connecting");
        else if (st === "CONNECTED") { setState("connected"); sessionRef.current = ctx.getCurrentSession(); }
      };
      sync();
      ctx.addEventListener(window.cast!.framework.CastContextEventType.CAST_STATE_CHANGED, sync);
    });
  }, []);

  // Push the currently-playing track to the receiver whenever it changes
  useEffect(() => {
    if (state !== "connected" || !current) return;
    const session = window.cast!.framework.CastContext.getInstance().getCurrentSession();
    if (!session) return;
    const media = new window.chrome!.cast!.media.MediaInfo(
      absoluteStreamUrl(current.id), "audio/mpeg",
    );
    media.metadata = new window.chrome!.cast!.media.MusicTrackMediaMetadata();
    media.metadata.title = current.title;
    media.metadata.artist = current.artist?.name;
    media.metadata.albumName = current.album?.title;
    const cover = current.album?.cover_id;
    if (cover && String(cover).startsWith("http")) {
      media.metadata.images = [new window.chrome!.cast!.Image(cover)];
    }
    const req = new window.chrome!.cast!.media.LoadRequest(media);
    session.loadMedia(req).catch(console.warn);
  }, [current, state]);

  const openPicker = () => {
    sfx.tap();
    try {
      window.cast!.framework.CastContext.getInstance().requestSession();
    } catch (e) { console.warn("cast pick failed", e); }
  };

  if (state === "unavailable") return null;
  const active = state === "connected" || state === "connecting";
  return (
    <button title={active ? "Cast (connecté)" : "Cast vers…"} onClick={openPicker}
            style={{ color: active ? "var(--accent)" : "inherit" }}>
      {/* Cast SVG glyph — same as Chrome */}
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M2 16c3 0 5 2 5 5" />
        <path d="M2 12c5 0 9 4 9 9" />
        <path d="M2 8c8 0 14 6 14 14" />
        <rect x="1" y="4" width="22" height="15" rx="2" />
      </svg>
    </button>
  );
}
